import api from 'services/api';
import { array, enforce, num, object, runSchema, str } from 'validation';

// Source of truth for the wire shape: nap-backend `routes/events.js` GET /events, which responds
// `{ events, pagination, config, filtersConfig }`. Only `events` is consumed here, and each event
// carries far more than an LOV row needs (config, parameters, action_type, references, …). The
// projection narrows to the identifiers an LOV row renders. `?type=processEvent` filters server-side
// to the process events (the only kind a runtime-limit "event on exceed" can reference).

export interface EventSummary {
  readonly event_id: number;
  readonly name: string;
  readonly zone_name: string;
}

const summarySchema = object<EventSummary>({
  event_id: num(),
  name: str(),
  zone_name: str(),
});

interface EventsResponse {
  readonly events: readonly EventSummary[];
}

const responseSchema = object<EventsResponse>({
  events: array(summarySchema),
});

const LOV_LIMIT = 1000;

/**
 * Fetch all process events the current user can see, for a picker. No pagination — the LOV needs the
 * complete set so Typeahead's client-side filter has every option to match against. If the universe of
 * process events ever grows past a UX-bearable dropdown size, this is the call site to migrate to
 * server-side search.
 */
export const getEventsLOV = async (): Promise<readonly EventSummary[]> => {
  const res = await api.get('/events', {
    params: { type: 'processEvent', limit: LOV_LIMIT },
  });
  const parsed = runSchema(responseSchema, res.data, 'getEventsLOV');
  if (!parsed.ok) {
    throw new Error('getEventsLOV: response failed validation');
  }
  return enforce(parsed.value, parsed.warnings, ['events']).events;
};
